import React from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardHeader, CardTitle, CardContent } from "./card";
import { LucideProps } from 'lucide-react';
import { cn } from '../../lib/utils';
import { setEnrichmentDialogOpen } from "../../store/flowSlice";
import { useTranslation } from "../../i18n/hooks";

interface Metric {
  title: string;
  score: number | null;
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
  category: string;
  suggestion: string;
  enrichmentFeatures: string[] | string | null;
  description: string;
  isUpcoming?: boolean; // Added this field

}

interface MetricsCardProps {
  title: string;
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
  iconColor: string;
  category: string;
  metrics: Metric[];
}

const getColorClasses = (score: number | null) => {
  if (score === null || score === undefined)
    return {
      bg: "bg-gray-500",
      text: "text-gray-600",
      lighter: "bg-gray-50",
      border: "border-gray-100",
      progress: "bg-gray-500",
      gradient: "from-gray-400 to-gray-600"
    };
  if (score >= 80)
    return {
      bg: "bg-emerald-500",
      text: "text-emerald-600",
      lighter: "bg-emerald-50",
      border: "border-emerald-100",
      progress: "bg-emerald-500",
      gradient: "from-emerald-400 to-emerald-600"
    };
  if (score >= 20)
    return {
      bg: "bg-amber-500",
      text: "text-amber-600",
      lighter: "bg-amber-50",
      border: "border-amber-100",
      progress: "bg-amber-500",
      gradient: "from-amber-400 to-amber-600"
    };
  return {
    bg: "bg-red-500",
    text: "text-red-600",
    lighter: "bg-red-50",
    border: "border-red-100",
    progress: "bg-red-500",
    gradient: "from-red-400 to-red-600"
  };
};

const MetricsCard: React.FC<MetricsCardProps> = ({ 
  title, 
  icon: Icon, 
  iconColor, 
  category, 
  metrics 
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filteredMetrics = metrics.filter(metric => metric.category === category);
  
  const handleSuggestionClick = (enrichmentFeatures: string[] | string | null) => {
    if (!enrichmentFeatures) return;
    const features = Array.isArray(enrichmentFeatures) 
      ? enrichmentFeatures 
      : [enrichmentFeatures];
    
    dispatch(setEnrichmentDialogOpen({
      isOpen: true,
      selectedFeatures: features,
    }));
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300 ease-in-out">
      <CardHeader className="border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className={cn(
            "p-2 rounded-lg transition-colors duration-300",
            getColorClasses(
              filteredMetrics.reduce((acc, curr) => acc + (curr.score ?? 0), 0) / filteredMetrics.length
            ).lighter
          )}>
            <Icon className={cn("h-6 w-6", iconColor)} />
          </div>
          <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-8 pt-6">
        {filteredMetrics.map((metric, index) => {
          const colors = getColorClasses(metric.score);
          const MetricIcon = metric.icon;
          
          return (
            <div key={metric.title} className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-1.5 rounded-md transition-colors duration-300",
                    colors.lighter
                  )}>
                    <MetricIcon className={cn("h-4 w-4", colors.text)} />
                  </div>
                  <span className="font-medium text-gray-800">{metric.title}</span>
                </div>
                <div className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300",
                  colors.lighter,
                  colors.text
                )}>
                  {metric.score}%
                </div>
              </div>
              
              {/* Progress bar with CSS animation */}
              <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full bg-gradient-to-r transition-all duration-1000 ease-out",
                    colors.gradient
                  )}
                  style={{ 
                    width: `${metric.score}%`,
                    transitionProperty: 'width, background-color'
                  }}
                />
              </div>

              {/* Description and suggestion */}
              <div className="flex flex-col md:flex-row justify-between items-start text-sm">
                <span className="text-gray-600 flex-1 pr-4">{metric.description}</span>
                <button
                  onClick={() => handleSuggestionClick(metric.enrichmentFeatures)}
                  className={cn(
                    "text-blue-600 hover:text-blue-700 font-medium text-left",
                    "transition-all duration-200 ease-in-out",
                    "hover:bg-blue-50 px-0 md:px-3 py-1 rounded-full",
                    (metric.isUpcoming || !metric.enrichmentFeatures) && "opacity-50 pointer-events-none cursor-not-allowed"
                  )}
                >
                  {metric.isUpcoming ? t('dashboard.coming_soon') : metric.suggestion}
                </button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MetricsCard;